// lib/syncApi.ts — funnel Pass 2 (client ↔ v1/sync)
// ─────────────────────────────────────────────────────────────────────────
// Transport and push orchestration for the two server-side documents:
// the financial profile (one per user) and plans (one per scenario id).
//
// CONTRACTS THAT MATTER HERE:
//   • The server stores opaque jsonb and returns it in Postgres's canonical
//     form — documents round-trip SEMANTICALLY, never byte-identically.
//   • Every PUT returns the WINNING document (LWW on clientUpdatedAt);
//     callers adopt what comes back instead of assuming their write won.
//   • Plans have no per-row client clock, so pushes stamp Date.now() at
//     push time (≈ edit time within the debounce); the server's LWW orders
//     pushes across devices. On fetch, server wins per id and local-only
//     plans are pushed up — data is never silently dropped. The accepted
//     v1 tradeoff: a plan deleted on another device can be resurrected by
//     a device that still holds it locally.
//
// PUSH ORCHESTRATION lives here too (used by localStorageMiddleware):
//   • scheduleProfilePush — 2s debounced PUT of the whole profile document,
//     clientUpdatedAt = the slice's own _lastUpdated.
//   • schedulePlanSync — 1.5s debounced diff of the scenario list against a
//     shadow of the last-pushed state: changed/new → PUT, missing → DELETE.
//     The shadow primes (without pushing) on the first observation and is
//     re-primed by fetchScenarios after every merge, including auth flips.
//
// Everything here is authed-only and fails SOFT: offline or 401 falls back
// to localStorage silently — the local world is always valid on its own.
// ─────────────────────────────────────────────────────────────────────────

import { auth } from './firebase';

const API_BASE: string =
	(import.meta.env.VITE_API_BASE as string | undefined) ??
	'http://localhost:51856';

export interface SyncedProfile {
	data: unknown;
	clientUpdatedAt: number;
}

export interface SyncedPlan {
	id: string;
	data: unknown;
	clientUpdatedAt: number;
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
	const token = (await auth.currentUser?.getIdToken()) ?? null;
	if (!token) throw new Error('sync: no authenticated user');
	const res = await fetch(`${API_BASE}${path}`, {
		...init,
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
			...(init?.headers ?? {}),
		},
	});
	if (!res.ok && res.status !== 204) {
		throw new Error(`sync: ${init?.method ?? 'GET'} ${path} → ${res.status}`);
	}
	return res;
}

// ─── Profile ──────────────────────────────────────────────────────────────

export async function syncGetProfile(): Promise<SyncedProfile | null> {
	const res = await authedFetch('/v1/sync/profile');
	if (res.status === 204) return null;
	return (await res.json()) as SyncedProfile;
}

export async function syncPutProfile(
	data: unknown,
	clientUpdatedAt: number,
): Promise<SyncedProfile> {
	const res = await authedFetch('/v1/sync/profile', {
		method: 'PUT',
		body: JSON.stringify({ data, clientUpdatedAt }),
	});
	return (await res.json()) as SyncedProfile;
}

// ─── Plans ────────────────────────────────────────────────────────────────

export async function syncListPlans(): Promise<SyncedPlan[]> {
	const res = await authedFetch('/v1/sync/plans');
	const body = (await res.json()) as { plans: SyncedPlan[] };
	return body.plans;
}

export async function syncPutPlan(
	id: string,
	data: unknown,
	clientUpdatedAt: number,
): Promise<SyncedPlan> {
	const res = await authedFetch(`/v1/sync/plans/${encodeURIComponent(id)}`, {
		method: 'PUT',
		body: JSON.stringify({ data, clientUpdatedAt }),
	});
	return (await res.json()) as SyncedPlan;
}

export async function syncDeletePlan(id: string): Promise<void> {
	await authedFetch(`/v1/sync/plans/${encodeURIComponent(id)}`, {
		method: 'DELETE',
	});
}

// ─── Push orchestration (consumed by localStorageMiddleware) ─────────────

const PROFILE_DEBOUNCE_MS = 2_000;
const PLANS_DEBOUNCE_MS = 1_500;

let profileTimer: number | null = null;

/** Debounced whole-document profile push. `doc` must already be transient-
 *  free (the middleware's persistable). Fire-and-forget; failures are
 *  logged and the local world remains authoritative until the next push. */
export function scheduleProfilePush(
	doc: unknown,
	clientUpdatedAt: number,
): void {
	if (profileTimer !== null) window.clearTimeout(profileTimer);
	profileTimer = window.setTimeout(() => {
		profileTimer = null;
		void syncPutProfile(doc, clientUpdatedAt).catch((err) => {
			console.warn('[sync] profile push failed (kept locally):', err);
		});
	}, PROFILE_DEBOUNCE_MS);
}

interface PlanLike {
	id: string;
	_syncStatus?: unknown;
}

/** Serialized form used for change detection AND as the pushed document —
 *  transient sync flags stay client-side. */
function planDocOf(plan: PlanLike): string {
	const { _syncStatus: _ignored, ...rest } = plan;
	return JSON.stringify(rest);
}

// Shadow of the last state pushed to (or fetched from) the server, keyed by
// plan id. null = never primed this session; the first observation primes
// WITHOUT pushing so boot doesn't re-push the world (fetchScenarios already
// pushed anything local-only during its merge).
let planShadow: Map<string, string> | null = null;
let plansTimer: number | null = null;

/** Called by fetchScenarios after merging server+local — makes the merged
 *  list the new baseline (also resets the baseline across auth flips, since
 *  the bridge refetches on every flip). */
export function primePlanShadow(plans: PlanLike[]): void {
	planShadow = new Map(plans.map((p) => [p.id, planDocOf(p)]));
}

/** Debounced diff-push of the scenario list against the shadow. */
export function schedulePlanSync(plans: PlanLike[]): void {
	if (planShadow === null) {
		// First observation this session: baseline, don't push.
		primePlanShadow(plans);
		return;
	}
	if (plansTimer !== null) window.clearTimeout(plansTimer);
	// Snapshot now — state may mutate again before the timer fires; the
	// next action will reschedule with the fresher snapshot anyway.
	const snapshot = plans.map((p) => ({ id: p.id, doc: planDocOf(p) }));
	plansTimer = window.setTimeout(() => {
		plansTimer = null;
		const shadow = planShadow as Map<string, string>;
		const seen = new Set<string>();
		for (const { id, doc } of snapshot) {
			seen.add(id);
			if (shadow.get(id) !== doc) {
				shadow.set(id, doc);
				void syncPutPlan(id, JSON.parse(doc), Date.now()).catch(
					(err) => {
						console.warn(
							`[sync] plan ${id} push failed (kept locally):`,
							err,
						);
					},
				);
			}
		}
		for (const id of [...shadow.keys()]) {
			if (!seen.has(id)) {
				shadow.delete(id);
				void syncDeletePlan(id).catch((err) => {
					console.warn(`[sync] plan ${id} delete failed:`, err);
				});
			}
		}
	}, PLANS_DEBOUNCE_MS);
}
